import type { IEscalationLevels, IWorkItemRow } from '../../api/work-item-contracts';
import { workItemsApi } from '../../api/work-items-api';
import { useAdminData } from '../../components/admin-page';

/**
 * The escalation hierarchy as the queue reads it (SR-410): how many levels there are, so Escalate
 * can be held off an item already at the last one and the pane can say "level 2 of 3". survey-api
 * picks the level and the person on every escalation, and refuses one past the last (409); this is
 * a render decision over the levels as read, never the rule.
 *
 * **Read once a page load**, by whichever of the queue, the pane and the bulk bar asks first, and
 * shared: the levels change on the Escalation levels page alone, which forgets the read when it
 * saves (`forgetEscalationLevels`). A failed read is forgotten too, so the next screen asks again;
 * until one lands, nothing is held off — survey-api still refuses a step past the last level.
 */
let held: Nullable<Promise<IEscalationLevels>> = null;

function readLevels(): Promise<IEscalationLevels> {
  if (held === null) {
    const read = workItemsApi.escalationLevels();
    held = read;
    read.catch(() => {
      if (held === read) held = null;
    });
  }
  return held;
}

/** The read forgotten — the Escalation levels page's, once it saves. */
export function forgetEscalationLevels(): void {
  held = null;
}

/** The levels as read this page load — null while out or failed. */
export function useEscalationLevels(): Nullable<IEscalationLevels> {
  return useAdminData(() => readLevels(), []).data;
}

/** The hierarchy's depth — 0 for none, null while it is not known. */
export function lastLevelOf(levels: Nullable<IEscalationLevels>): Nullable<number> {
  return levels === null ? null : levels.levels.length;
}

/** Why Escalate is held off `rows` — any of them already at the last level, since a decision over
 *  several is all or nothing — or null where it is not. */
export function escalateBlocked(rows: readonly IWorkItemRow[], last: Nullable<number>): Nullable<string> {
  if (last === null || last === 0 || rows.length === 0) return null;
  const atLast = rows.filter((row) => (row.escalationLevel ?? 0) >= last).length;
  if (atLast === 0) return null;
  return rows.length === 1
    ? `At the last escalation level (${last})`
    : `${atLast} of the ticked items are at the last escalation level (${last})`;
}
